import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, Funnel } from "@phosphor-icons/react";

const EMPTY = {
  name: "", sender_contains: "", subject_contains: "", body_contains: "",
  map_to_status: "healthy", priority: 100, enabled: true,
};
const STATUSES = ["healthy", "warning", "failure", "missed", "unknown"];

export default function Rules() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/rules"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (r) => {
    setForm({
      name: r.name,
      sender_contains: r.sender_contains || "",
      subject_contains: r.subject_contains || "",
      body_contains: r.body_contains || "",
      map_to_status: r.map_to_status, priority: r.priority, enabled: r.enabled,
    });
    setEditing(r); setError("");
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = {
        ...form,
        sender_contains: form.sender_contains || null,
        subject_contains: form.subject_contains || null,
        body_contains: form.body_contains || null,
        priority: Number(form.priority) || 100,
      };
      if (editing === "new") await api.post("/rules", payload);
      else await api.put(`/rules/${editing.id}`, payload);
      setEditing(null); await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    await api.delete(`/rules/${id}`); await load();
  };

  return (
    <Layout>
      <PageHeader
        title="Parsing rules"
        subtitle="Deterministic classification — tried before AI"
        actions={
          <button onClick={openNew} data-testid="rules-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New rule
          </button>
        }
      />
      <div className="p-6">
        {loading ? <div className="text-sm text-slate-500">loading…</div>
         : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Funnel size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No rules yet</h3>
            <p className="text-sm text-slate-400 mb-4">Add deterministic rules to override AI classification for specific senders or subjects.</p>
            <button onClick={openNew} data-testid="rules-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add rule
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="rules-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4 w-16">Pri</th>
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Match</th>
                  <th className="text-left py-3 px-4">→ Status</th>
                  <th className="text-left py-3 px-4 w-24">Enabled</th>
                  <th className="text-right py-3 px-4 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-white/5">
                    <td className="py-3 px-4 text-xs text-slate-400">{r.priority}</td>
                    <td className="py-3 px-4 text-white">{r.name}</td>
                    <td className="py-3 px-4 text-xs text-slate-400 space-y-0.5">
                      {r.sender_contains && <div>from~ <span className="text-slate-100">{r.sender_contains}</span></div>}
                      {r.subject_contains && <div>subj~ <span className="text-slate-100">{r.subject_contains}</span></div>}
                      {r.body_contains && <div>body~ <span className="text-slate-100">{r.body_contains}</span></div>}
                    </td>
                    <td className="py-3 px-4"><StatusBadge status={r.map_to_status} /></td>
                    <td className="py-3 px-4">
                      <span className={`text-xs ${r.enabled ?"text-emerald-400" :"text-slate-400"}`}>
                        {r.enabled ? "ON" : "OFF"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => openEdit(r)} data-testid={`rule-edit-${r.id}`}
                        className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`rule-delete-${r.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400 ml-1"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-lg p-6 space-y-3" data-testid="rule-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New rule" : "Edit rule"}</h3>
            <p className="text-xs text-slate-500">A rule fires when ALL filled conditions match (case-insensitive substring).</p>
            <FieldR label="Name" required><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name-input" /></FieldR>
            <FieldR label="Sender contains"><input value={form.sender_contains} onChange={(e) => setForm({ ...form, sender_contains: e.target.value })} data-testid="rule-sender-input" /></FieldR>
            <FieldR label="Subject contains"><input value={form.subject_contains} onChange={(e) => setForm({ ...form, subject_contains: e.target.value })} data-testid="rule-subject-input" /></FieldR>
            <FieldR label="Body contains"><input value={form.body_contains} onChange={(e) => setForm({ ...form, body_contains: e.target.value })} data-testid="rule-body-input" /></FieldR>
            <div className="grid grid-cols-2 gap-3">
              <FieldR label="Map to status">
                <select value={form.map_to_status} onChange={(e) => setForm({ ...form, map_to_status: e.target.value })} data-testid="rule-status-select">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldR>
              <FieldR label="Priority (lower = first)"><input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="rule-priority-input" /></FieldR>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="rule-enabled-checkbox" />
              Enabled
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setEditing(null)} data-testid="rule-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="rule-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldR({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
